import {
  addDays,
  ATMOSPHERE_DEFAULTS,
  restMode,
  skyPlace,
  type Atmosphere,
  type RestMode,
} from '@shh/shared';
import { AmbientScene, FestiveScene, Icon, computeDaylight, skyPhase } from '@shh/ui';
import { useEffect, useMemo, useState } from 'react';
import { Hints } from '../components/Hints';
import { candlesOf, festiveHeadline, sceneOf, useFestiveDay } from '../components/Festive';
import { useUpcoming, useWallGong } from '../components/WallReminder';
import { useClock } from '../data/clock';
import { eventsOn, participants, type E } from '../data/derive';
import { useFamily } from '../data/family';
import { hintsFor } from '../data/hints';
import { useWeather } from '../data/weather';
import { dateFormat } from '../lib/locale';
import { setLens } from './view';
import { todayText } from '../i18n/today';
import { calendarText } from '../i18n/calendar';

/** "Ruhebild jetzt ansehen" in the settings – works on every device, also without a wall. */
const SHOW_EVENT = 'shh:rest';
export const showRestScreen = () => window.dispatchEvent(new Event(SHOW_EVENT));

export const atmosphereOf = (f: ReturnType<typeof useFamily>): Atmosphere =>
  f.household?.atmosphere ?? ATMOSPHERE_DEFAULTS;

/**
 * Family Atmosphere: after some minutes without a touch the wall display rests – a calm sky
 * with clock, weather, the next event and the pinboard; inside the night window it goes almost
 * black. Any touch or key brings the dashboard back at once.
 */
export function useRest(auto: boolean): { mode: RestMode | 'awake'; wake: () => void } {
  const f = useFamily();
  const settings = atmosphereOf(f);
  const now = useClock(f.timeZone);
  const [resting, setResting] = useState(false);
  const enabled = auto && settings.enabled;
  const idleMs = settings.idleMinutes * 60_000;

  // Like data-context / data-daylight: the current rule is visible on <html> (debugging, tests).
  useEffect(() => {
    if (!auto) return;
    const html = document.documentElement;
    html.dataset.rest = enabled ? String(settings.idleMinutes) : 'off';
    return () => void delete html.dataset.rest;
  }, [auto, enabled, settings.idleMinutes]);

  useEffect(() => {
    const show = () => setResting(true);
    window.addEventListener(SHOW_EVENT, show);
    return () => window.removeEventListener(SHOW_EVENT, show);
  }, []);

  useEffect(() => {
    if (!enabled || resting) return;
    let t = window.setTimeout(() => setResting(true), idleMs);
    const bump = () => {
      window.clearTimeout(t);
      t = window.setTimeout(() => setResting(true), idleMs);
    };
    const evs = ['pointerdown', 'keydown', 'scroll', 'wheel'] as const;
    evs.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    return () => {
      window.clearTimeout(t);
      evs.forEach((e) => window.removeEventListener(e, bump));
    };
  }, [enabled, resting, idleMs]);

  // whoever walks up after the rest sees the whole family (Mike, 24.10.: "an der Wand nach der Ruhepause")
  useEffect(() => {
    if (resting) setLens('all');
  }, [resting]);

  // a key brings it back as well (keyboard, remote, switch access)
  useEffect(() => {
    if (!resting) return;
    const wake = () => setResting(false);
    window.addEventListener('keydown', wake);
    return () => window.removeEventListener('keydown', wake);
  }, [resting]);

  return {
    mode: resting ? restMode(now, settings) : 'awake',
    wake: () => setResting(false),
  };
}

const dateFmt = (tz: string) =>
  dateFormat({
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone: tz,
  });

/** The next thing on the calendar: later today, otherwise tomorrow's first. */
function nextEvent(
  f: ReturnType<typeof useFamily>,
  now: string,
): { e: E; when: string; tomorrow: boolean } | null {
  const today = eventsOn(f, f.today).find((e) => e.start && e.start >= now);
  if (today) return { e: today, when: `${today.start}`, tomorrow: false };
  const tomorrow = eventsOn(f, addDays(f.today, 1))[0];
  if (tomorrow)
    return {
      e: tomorrow,
      when: tomorrow.start ? calendarText.tomorrowAt(tomorrow.start) : calendarText.tomorrow,
      tomorrow: true,
    };
  return null;
}

export function RestScreen({ mode, onWake }: { mode: RestMode; onWake: () => void }) {
  const f = useFamily();
  const now = useClock(f.timeZone);
  const weather = useWeather(f.household?.location, f.timeZone);
  const place = f.household?.location;
  const sky = skyPlace(place, f.timeZone);
  const phase = useMemo(
    () => skyPhase(computeDaylight(new Date(), sky.latitude, sky.longitude).anchor),
    // recomputed with every clock tick
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [now, sky.latitude, sky.longitude],
  );

  const sleep = mode === 'sleep';
  const next = nextEvent(f, now);
  // 15 minutes before: the next event becomes a reminder – and the gong sounds once (1.13)
  const upcoming = useUpcoming(now);
  useWallGong(upcoming, f.role === 'device' && !sleep);
  const festive = useFestiveDay();
  // "Als Nächstes" already names tomorrow's first event – no second line about it
  const hints = hintsFor(f, now, weather, 3)
    .filter((h) => !(next?.tomorrow && h.id === `tomorrow:${next.e.title}`))
    .slice(0, 2);
  const date = dateFmt(f.timeZone).format(new Date());

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={sleep ? todayText.nightRest : todayText.rest}
      className="fixed inset-0 z-50"
    >
      <AmbientScene phase={phase} weather={weather?.kind} sleep={sleep} className="h-full">
        {sleep ? (
          // night: only a dim clock in the middle, still wandering against burn-in
          <div className="shh-drift grid h-full place-items-center">
            <p className="sr-only">{date}</p>
            <time className="font-display text-clock leading-none font-black tabular-nums">
              {now}
            </time>
          </div>
        ) : (
          // Landscape (wall tablet): clock left, plan right – both fit any height. Portrait and
          // phones: stacked, the clock area gives way first (it scales with the height, too).
          <div className="relative h-full">
            {festive && (
              <FestiveScene
                kind={sceneOf(festive)}
                variant="backdrop"
                candles={candlesOf(festive)}
              />
            )}
            <div className="shh-drift relative grid h-full grid-rows-[minmax(0,1fr)_auto] gap-section overflow-hidden px-safe py-section lg:grid-cols-[3fr_2fr] lg:grid-rows-1 lg:items-center">
              <div className="flex min-h-0 flex-col items-center justify-center gap-gap-md">
                {festive && (
                  <p className="text-center font-display text-3xl font-black text-balance drop-shadow-sm">
                    {festiveHeadline(festive)}
                  </p>
                )}
                <p className="text-xl font-semibold text-ambient-sky-text-soft">{date}</p>
                <time className="font-display text-clock leading-none font-black tabular-nums">
                  {now}
                </time>
                {weather && (
                  <p className="flex items-center gap-gap-sm text-xl font-semibold">
                    <Icon icon={weather.icon} size="lg" weight="duotone" />
                    <span>
                      {weather.temp}° · {weather.summary}
                    </span>
                  </p>
                )}
              </div>
              {/* most important first: if a small screen runs out of room, the pinboard goes */}
              <div className="flex min-h-0 flex-col gap-gap-lg overflow-hidden">
                {next && (
                  <p
                    className={
                      upcoming && upcoming.e.id === next.e.id
                        ? 'flex flex-col rounded-card bg-ambient-sky-card/70 p-inset-md'
                        : 'flex flex-col'
                    }
                  >
                    <span className="text-lg text-ambient-sky-text-soft">
                      {upcoming && upcoming.e.id === next.e.id
                        ? `${calendarText.inMinutesCap(upcoming.minutes)} · ${next.when}`
                        : todayText.upNextAt(next.when)}
                    </span>
                    <strong className="font-display text-2xl font-black">{next.e.title}</strong>
                    {next.e.assignees !== 'all' && (
                      <span className="text-lg text-ambient-sky-text-soft">
                        {participants(f, next.e)
                          .map((m) => m.name)
                          .join(', ')}
                      </span>
                    )}
                  </p>
                )}
                <Hints hints={hints} size="lg" />
                {f.notes.length > 0 && (
                  <ul aria-label={todayText.pinboard} className="flex flex-wrap gap-gap-md">
                    {f.notes.slice(0, 2).map((n) => (
                      <li
                        key={n.id}
                        className="max-w-narrow rounded-card bg-ambient-sky-card/50 px-inset-md py-inset-sm text-lg"
                      >
                        {n.text}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        )}
      </AmbientScene>
      {/*
        One button over the whole screen: a finger taps anywhere, a keyboard finds it focused.
        Native click (after the finger is lifted), so the tap never falls through to the
        dashboard underneath.
      */}
      <button
        type="button"
        // eslint-disable-next-line jsx-a11y/no-autofocus -- the only control while resting
        autoFocus
        aria-label={todayText.backToOverview}
        onClick={onWake}
        className="absolute inset-0 cursor-pointer outline-none focus-visible:outline-3 focus-visible:-outline-offset-8 focus-visible:outline-ambient-sky-text"
      />
    </div>
  );
}
